// src/modules/competition/pages/CreateCompetitionPage/index.js
import React, { useState, useCallback } from 'react';
import { Container, Card, Form, Button, Row, Col, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import Flatpickr from 'react-flatpickr';
import 'flatpickr/dist/flatpickr.min.css';
import { useService } from '../../../../shared/hooks/useService';
import Loading from '../../../../shared/components/Loading';
import { useWarmupTimer } from '../../../../shared/hooks/useWarmupTimer';
import './styles.css';

const initialFormState = {
    name: '',
    pointTarget: 100,
    startDate: null,
    endDate: null
};

const CreateCompetitionPage = () => {
    const [formData, setFormData] = useState(initialFormState);
    const [isLoading, setIsLoading] = useState(false);
    const { isWarmup } = useWarmupTimer(isLoading);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const service = useService();
    const navigate = useNavigate();

    const handleChange = useCallback((e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    }, []);

    const handleDateChange = useCallback((name, date) => {
        setFormData(prev => ({
            ...prev,
            [name]: date[0] || null
        }));
    }, []);

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);
        setSuccess(null);

        try {
            // Validation
            if (!formData.name || !formData.name.trim()) {
                throw new Error('Competition name is required');
            }
            if (!formData.pointTarget || formData.pointTarget <= 0) {
                throw new Error('Point target must be greater than 0');
            }
            if (!formData.startDate) {
                throw new Error('Start date is required');
            }
            if (!formData.endDate) {
                throw new Error('End date is required');
            }
            if (formData.startDate >= formData.endDate) {
                throw new Error('End date must be after start date');
            }

            const competitionData = {
                name: formData.name.trim(),
                pointTarget: parseFloat(formData.pointTarget),
                startDate: formData.startDate.toISOString(),
                endDate: formData.endDate.toISOString()
            };

            const response = await service.createCompetition(competitionData);

            setSuccess('Competition created successfully!');

            // Navigate to the new competition after a short delay
            setTimeout(() => {
                if (response.competition?.competitionId) {
                    navigate(`/competition/${response.competition.competitionId}`);
                } else {
                    navigate('/competitions');
                }
            }, 1500);

        } catch (err) {
            setError(err.message || 'Failed to create competition');
        } finally {
            setIsLoading(false);
        }
    }, [formData, service, navigate]);

    const handleCancel = useCallback(() => {
        navigate('/competitions');
    }, [navigate]);

    if (isLoading) return <Loading isWarmup={isWarmup} />;

    return (
        <Container className="mt-4">
            <Card className="shadow-sm create-competition-card">
                <Card.Header>
                    <h4 className="mb-0">
                        <i className="bi bi-trophy me-2"></i>
                        Create New Competition
                    </h4>
                </Card.Header>
                <Card.Body>
                    {error && (
                        <Alert variant="danger" onClose={() => setError(null)} dismissible>
                            {error}
                        </Alert>
                    )}
                    {success && (
                        <Alert variant="success">
                            {success}
                        </Alert>
                    )}

                    <Form onSubmit={handleSubmit}>
                        {/* Competition Name */}
                        <Form.Group className="mb-4">
                            <Form.Label>
                                <i className="bi bi-card-text me-2 text-primary"></i>
                                Competition Name
                            </Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                placeholder="Enter competition name"
                                required
                                maxLength={100}
                            />
                            <Form.Text>
                                Choose a descriptive name for your competition
                            </Form.Text>
                        </Form.Group>

                        {/* Point Target */}
                        <Form.Group className="mb-4">
                            <Form.Label>
                                <i className="bi bi-bullseye me-2 text-primary"></i>
                                Point Target
                            </Form.Label>
                            <Form.Control
                                type="number"
                                name="pointTarget"
                                value={formData.pointTarget}
                                onChange={handleChange}
                                min="1"
                                step="1"
                                required
                            />
                            <Form.Text>
                                The point goal participants should aim for
                            </Form.Text>
                        </Form.Group>

                        {/* Date Range */}
                        <Row className="mb-4">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>
                                        <i className="bi bi-calendar-event me-2 text-primary"></i>
                                        Start Date
                                    </Form.Label>
                                    <Flatpickr
                                        value={formData.startDate}
                                        onChange={(date) => handleDateChange('startDate', date)}
                                        options={{
                                            dateFormat: "Y-m-d"
                                        }}
                                        className="form-control"
                                        placeholder="Select start date"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>
                                        <i className="bi bi-calendar-check me-2 text-primary"></i>
                                        End Date
                                    </Form.Label>
                                    <Flatpickr
                                        value={formData.endDate}
                                        onChange={(date) => handleDateChange('endDate', date)}
                                        options={{
                                            dateFormat: "Y-m-d",
                                            minDate: formData.startDate || "today"
                                        }}
                                        className="form-control"
                                        placeholder="Select end date"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        {/* Submit Buttons */}
                        <div className="d-flex gap-3">
                            <Button
                                type="submit"
                                variant="primary"
                                disabled={isLoading}
                                className="flex-grow-1"
                            >
                                {isLoading ? (
                                    <>
                                        <i className="bi bi-arrow-repeat spinner me-2"></i>
                                        Creating...
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-plus-circle me-2"></i>
                                        Create Competition
                                    </>
                                )}
                            </Button>
                            <Button
                                type="button"
                                variant="outline-secondary"
                                onClick={handleCancel}
                                disabled={isLoading}
                            >
                                Cancel
                            </Button>
                        </div>
                    </Form>
                </Card.Body>
            </Card>
        </Container>
    );
};

export default CreateCompetitionPage;
